import React, { useMemo } from 'react'

import { LongTextAttributeDisplay } from 'features/views/attributes/LongTextAttributeDisplay'
import { RichTextAttributeDisplay } from 'features/views/attributes/RichTextAttributeDisplay'
import { TextAttributeDisplay } from 'features/views/attributes/TextAttributeDisplay'
import { RichTextValue } from 'features/views/attributes/types'
import { PipelineWidgetStageData } from 'features/views/LayoutEditor/widgets/PipelineWidget/types'

import { Body } from 'ui/components/Text'

import { useDescriptionAttributeState } from './hooks/useDescriptionAttributeState'

type DescriptionAttributeProps = Pick<
    React.ComponentProps<typeof Body>,
    'style' | 'className' | 'size' | 'color' | 'weight' | 'mt' | 'trim' | 'maxWidth' | 'minWidth'
> & {
    item: PipelineWidgetStageData
    maxLines?: number
}

export const DescriptionAttribute: React.FC<DescriptionAttributeProps> = React.memo(
    function DescriptionAttribute({ style, item, ...props }) {
        const { field, value } = useDescriptionAttributeState(item)

        const baseStyle: React.CSSProperties = useMemo(
            () => ({
                ...style,
                wordBreak: 'break-word',
            }),
            [style]
        )

        if (!value) {
            return null
        }

        switch (field?.type) {
            case 'string':
                return (
                    <TextAttributeDisplay
                        key={field._sid}
                        field={field}
                        value={value as string}
                        style={baseStyle}
                        {...props}
                    />
                )

            case 'long_text':
                return (
                    <LongTextAttributeDisplay
                        key={field._sid}
                        field={field}
                        value={value as string}
                        style={baseStyle}
                        {...props}
                    />
                )

            case 'document':
                return (
                    <RichTextAttributeDisplay
                        key={field._sid}
                        field={field}
                        value={value as RichTextValue}
                        style={baseStyle}
                        {...props}
                    />
                )

            default:
                return (
                    <Body style={baseStyle} display="block" {...props}>
                        {value}
                    </Body>
                )
        }
    }
)
